import { CountryCode } from '@shinetools/geo-library';

import { KycProvider, KycStatus } from '__generated__/GQL';
import assertEventType from 'common/bento/lib/assertEventType';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import webView from 'common/webView';
import { versionIsGte } from 'common/webView/helpers/versionIsGte';
import canEditPersonalInformation from 'features/Onboarding/lib/canEditPersonalInformation';

import { VerificationStatus } from '../../Verification/lib/steps/types';
import { ACCEPTED_PASSPORT_AND_ID_CARD_COUNTRIES } from '../libs/acceptedPassportAndIdCardCountries';
import getIdentityStatus from '../libs/getIdentityStatus';
import { Model } from './model';

export enum Guard {
  HasNativeIdCheckSucceeded = 'hasNativeIdCheckSucceeded',
  HasNativeIdCheckFailed = 'hasNativeIdCheckFailed',
  ShouldDoNativeFourthlineWorkflow = 'shouldDoNativeFourthlineWorkflow',
  ShouldDoFourthlineVerification = 'shouldDoFourthlineVerification',
  ShouldGoThroughResidencyDocumentQuestions = 'shouldGoThroughResidencyDocumentQuestions',
  IsIdentityReviewInvalid = 'isIdentityReviewInvalid',
  ShouldSeeRecap = 'shouldSeeRecap',
}

const guards: MachineGuards<Model, Guard> = {
  hasNativeIdCheckFailed: (context, event) => {
    assertEventType(event, [
      'done.invoke.completeNativeIdCheck',
      'done.invoke.completeNativeFourthlineWorkflow',
    ]);

    return event.data.status === 'error';
  },

  hasNativeIdCheckSucceeded: (context, event) => {
    assertEventType(event, [
      'done.invoke.completeNativeIdCheck',
      'done.invoke.completeNativeFourthlineWorkflow',
    ]);

    return event.data.status === 'done';
  },

  isIdentityReviewInvalid: (context) => {
    const { companyUser } = context.getModuleData().viewer;

    return companyUser.kycIdStatus === KycStatus.Refused;
  },

  shouldDoFourthlineVerification: (context, event) => {
    assertEventType(event, 'done.invoke.startIdentityVerification');

    return event.data.provider === KycProvider.Fourthline;
  },

  shouldDoNativeFourthlineWorkflow: (context, event) => {
    assertEventType(event, 'done.invoke.startIdentityVerification');

    if (
      !webView.isInWebView ||
      !webView.config?.appVersion ||
      event.data.provider !== KycProvider.Fourthline
    ) {
      return false;
    }

    const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent);

    const isWebViewCompatible =
      versionIsGte(webView.config.appVersion, '1.261.0') ||
      /**
       * There was a bug in 1.260 impacting Android devices.
       */
      (isIOS && versionIsGte(webView.config.appVersion, '1.260.0'));

    return isWebViewCompatible;
  },

  shouldGoThroughResidencyDocumentQuestions: ({ getModuleData }) => {
    const { nationality } = getModuleData().viewer.profile;

    if (!nationality) {
      throw new Error(
        `nationality missing in shouldGoThroughResidencyCardQuestions`,
      );
    }

    return !ACCEPTED_PASSPORT_AND_ID_CARD_COUNTRIES.has(
      nationality as CountryCode,
    );
  },

  shouldSeeRecap: ({ getModuleData }) => {
    const { companyUser, companyUsers, isNewOnboardingAllowed } =
      getModuleData().viewer;

    const canEditInformation = canEditPersonalInformation({
      companyUser: companyUser ?? null,
      companyUsers: companyUsers ?? null,
      isNewOnboardingAllowed,
    });
    const identityStatus = getIdentityStatus({ companyUser });

    return (
      !canEditInformation ||
      [VerificationStatus.Completed, VerificationStatus.Validated].includes(
        identityStatus,
      )
    );
  },
};

export default guards;
